'use client';

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { greeting } from '@/app/_lib/config/shopping-assistant-config';
const HelpWidgetContext = createContext({});
const STORAGE_KEY = 'hive_ai_assistant_state';
const DEFAULT_MESSAGE = {
  role: 'assistant',
  content: greeting
};
export const HelpWidgetProvider = ({
  children
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPanel, setCurrentPanel] = useState('main');

  // AI Assistant state - persisted across widget open/close and page refreshes
  const [aiMessages, setAiMessages] = useState([DEFAULT_MESSAGE]);
  const [aiSessionId, setAiSessionId] = useState(null);
  const hasLoadedFromStorage = useRef(false);

  // Load AI state from localStorage on mount
  useEffect(() => {
    if (hasLoadedFromStorage.current) return;
    hasLoadedFromStorage.current = true;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.messages && parsed.messages.length > 0) {
          setAiMessages(parsed.messages);
        }
        if (parsed.sessionId) {
          setAiSessionId(parsed.sessionId);
        }
      }
    } catch (e) {
      // localStorage not available or corrupted data
    }
  }, []);

  // Persist AI state to localStorage when messages change
  useEffect(() => {
    if (!hasLoadedFromStorage.current) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        messages: aiMessages,
        sessionId: aiSessionId
      }));
    } catch (e_0) {
      // localStorage not available
    }
  }, [aiMessages, aiSessionId]);
  const openWidget = useCallback((panel = 'main') => {
    setCurrentPanel(panel);
    setIsOpen(true);
  }, []);

  // Minimize - closes widget but preserves AI state and current panel
  const minimizeWidget = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Close - fully closes and resets to main panel (but still preserves AI messages)
  const closeWidget = useCallback(() => {
    setIsOpen(false);
    // Reset to main panel after close animation completes
    setTimeout(() => setCurrentPanel('main'), 300);
  }, []);
  const setPanel = useCallback(panel_0 => {
    setCurrentPanel(panel_0);
  }, []);

  // Clear AI conversation and start fresh
  const clearAiConversation = useCallback(() => {
    setAiMessages([DEFAULT_MESSAGE]);
    setAiSessionId(crypto.randomUUID());
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e_1) {}
  }, []);
  return <HelpWidgetContext.Provider value={{
    isOpen,
    currentPanel,
    openWidget,
    closeWidget,
    minimizeWidget,
    setPanel,
    // AI Assistant state
    aiMessages,
    setAiMessages,
    aiSessionId,
    setAiSessionId,
    clearAiConversation
  }}>
            {children}
        </HelpWidgetContext.Provider>;
};
export const useHelpWidget = () => {
  const context = useContext(HelpWidgetContext);
  if (!context) {
    throw new Error("useHelpWidget must be used within a HelpWidgetProvider");
  }
  return context;
};